'use client';

import React, { useState, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { MessageCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';
import { useChatStore } from '@/store/chatStore';
import { ToastContainer } from '@/components/ui/Toast';
import { Navbar } from '@/components/layout/Navbar';

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawRedirect = searchParams.get('redirect') || '/feed';
  // Only allow relative paths — never redirect to external URLs
  const redirect = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/feed';

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { guestDisplayName } = useChatStore();
  const [username, setUsername] = useState(guestDisplayName || '');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, register } = useAuth();
  const { addToast } = useUIStore();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, username);
      }
      router.push(redirect);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-black px-4 pt-12 md:pt-24 pb-10 md:pb-16">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, #C4D600 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>

      {/* Logo */}
      <Link href="/" className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 rounded-full bg-brand-lime flex items-center justify-center">
          <span className="text-brand-black font-bold">SW</span>
        </div>
        <span className="font-semibold text-lg text-brand-white tracking-wide">STRANGER WORLD</span>
      </Link>

      {/* Card */}
      <div className="relative w-full max-w-sm sm:max-w-md bg-surface-elevated rounded-2xl border border-white/10 p-6 sm:p-8 shadow-modal">
        {/* Tabs */}
        <div className="flex rounded-xl bg-brand-surface-2/60 p-1 mb-8">
          {(['login', 'register'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => { setMode(tab); setError(''); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                mode === tab
                  ? 'bg-brand-lime text-brand-black shadow-sm'
                  : 'text-brand-gray hover:text-brand-white'
              }`}
            >
              {tab === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-brand-white">
            {mode === 'login' ? 'Welcome back' : 'Join Stranger World'}
          </h1>
          <p className="text-sm text-brand-gray mt-1">
            {mode === 'login'
              ? 'Sign in to access premium features'
              : 'Create an account to unlock premium'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === 'register' && (
            <Input
              label="Username (optional)"
              type="text"
              placeholder="Choose a display name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          )}

          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-brand-gray-light">Password</label>
              {mode === 'login' && (
                <Link
                  href="/forgot-password"
                  className="text-xs text-brand-lime hover:underline"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={mode === 'register' ? 'Min. 8 characters' : '••••••••'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={mode === 'register' ? 8 : undefined}
                className="w-full h-10 bg-brand-surface-2 border border-brand-gray-muted rounded-xl px-4 pr-10 text-sm text-brand-white placeholder:text-brand-gray focus:outline-none focus:ring-2 focus:ring-brand-lime/50 focus:border-brand-lime"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-brand-white transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-brand-red bg-brand-red/10 border border-brand-red/20 rounded-xl px-4 py-2.5">
              {error}
            </p>
          )}

          <Button type="submit" fullWidth loading={loading} size="md" className="mt-2">
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-brand-gray">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Google sign-in */}
        <a
          href={`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000'}/api/auth/google`}
          className="flex items-center justify-center gap-3 w-full h-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium text-brand-white mb-3"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
            <path d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" fill="#FFC107"/>
            <path d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" fill="#FF3D00"/>
            <path d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.5 26.7 36 24 36c-5.2 0-9.7-3.3-11.3-7.9l-6.6 5.1C9.5 39.6 16.2 44 24 44z" fill="#4CAF50"/>
            <path d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.2-4.2 5.6l6.2 5.2C37 38.1 44 33 44 24c0-1.2-.1-2.3-.4-3.5z" fill="#1976D2"/>
          </svg>
          Continue with Google
        </a>

        {/* Anonymous option */}
        <Link href="/chat">
          <Button variant="secondary" fullWidth>
            <MessageCircle size={14} />
            Continue Anonymously
          </Button>
        </Link>

        {/* Footer note */}
        <p className="text-xs text-brand-gray text-center mt-6">
          By continuing, you agree to our{' '}
          <Link href="#" className="text-brand-lime hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link href="#" className="text-brand-lime hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <>
      <Navbar />
      <Suspense fallback={<div className="min-h-screen bg-brand-black" />}>
        <LoginForm />
      </Suspense>
      <ToastContainer />
    </>
  );
}
